import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import config from '../config/environment';
import { shouldUseAccounts, SKIP_SSO_AUTO_KEY, type AuthModeConfig } from '../accounts';
import styles from './SignInPage.module.css';

const OAUTH_STATE_KEY = 'expenses_oauth_state';
const OAUTH_VERIFIER_KEY = 'expenses_oauth_verifier';
const OAUTH_COOKIE = 'expenses_oauth';

type OauthState = { n: string; v: string };

function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function encodeOauthState(payload: OauthState): string {
  return bytesToBase64Url(new TextEncoder().encode(JSON.stringify(payload)));
}

function decodeOauthState(state: string | null): OauthState | null {
  if (!state) return null;
  try {
    const b64 = state.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const json = new TextDecoder().decode(Uint8Array.from(atob(padded), (c) => c.charCodeAt(0)));
    const parsed = JSON.parse(json) as OauthState;
    if (parsed && typeof parsed.n === 'string' && typeof parsed.v === 'string') {
      return parsed;
    }
  } catch {
    // Google OAuth state is not ours.
  }
  return null;
}

function persistOauth(payload: OauthState) {
  sessionStorage.setItem(OAUTH_VERIFIER_KEY, payload.v);
  sessionStorage.setItem(OAUTH_STATE_KEY, payload.n);
  localStorage.setItem(OAUTH_VERIFIER_KEY, payload.v);
  localStorage.setItem(OAUTH_STATE_KEY, payload.n);
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${OAUTH_COOKIE}=${encodeURIComponent(JSON.stringify(payload))}; Path=/; SameSite=Lax${secure}; Max-Age=600`;
}

function readOauthCookie(): OauthState | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${OAUTH_COOKIE}=([^;]*)`));
  if (!match) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(match[1])) as OauthState;
    if (parsed && typeof parsed.n === 'string' && typeof parsed.v === 'string') {
      return parsed;
    }
  } catch {
    return null;
  }
  return null;
}

function clearOauth() {
  sessionStorage.removeItem(OAUTH_VERIFIER_KEY);
  sessionStorage.removeItem(OAUTH_STATE_KEY);
  localStorage.removeItem(OAUTH_VERIFIER_KEY);
  localStorage.removeItem(OAUTH_STATE_KEY);
  document.cookie = `${OAUTH_COOKIE}=; Path=/; Max-Age=0`;
}

function randomUrlSafe(bytes = 32): string {
  const arr = new Uint8Array(bytes);
  crypto.getRandomValues(arr);
  return bytesToBase64Url(arr);
}

async function pkcePair(): Promise<{ verifier: string; challenge: string }> {
  const verifier = randomUrlSafe(64);
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return { verifier, challenge: bytesToBase64Url(new Uint8Array(digest)) };
}

function callbackRedirectUri(): string {
  return `${window.location.origin}/auth/callback`;
}

const SignInPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, isLoading: authLoading, authConfig } = useAuth();
  const { showError } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [fetchedConfig, setFetchedConfig] = useState<AuthModeConfig | null>(null);
  const [configReady, setConfigReady] = useState(false);
  const startedSso = useRef(false);
  const handledCode = useRef<string | null>(null);

  const [skipAutoSso, setSkipAutoSso] = useState(() => {
    try {
      return sessionStorage.getItem(SKIP_SSO_AUTO_KEY) === '1';
    } catch {
      return false;
    }
  });

  const urlParams = new URLSearchParams(location.search);
  const code = urlParams.get('code');
  const oauthError = urlParams.get('error');
  const tenantId = urlParams.get('tenant_id');
  const effectiveConfig = fetchedConfig || authConfig;
  const sso = shouldUseAccounts(effectiveConfig);

  useEffect(() => {
    fetch(`${config.api.baseUrl}/auth/config`)
      .then((response) => response.json())
      .then((data) => {
        if (data?.success && data.data) {
          setFetchedConfig(data.data);
        }
      })
      .catch(() => {
        setFetchedConfig({ auth_mode: 'google' });
      })
      .finally(() => setConfigReady(true));
  }, []);

  useEffect(() => {
    if (!configReady) return;
    if (oauthError) {
      setError('Authentication failed. Please try again.');
      showError('שגיאה בהתחברות', {
        action: 'OAuth provider error',
        error: oauthError,
      });
      return;
    }
    if (code && handledCode.current !== code) {
      handledCode.current = code;
      handleOAuthCallback(code, urlParams.get('state'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, configReady]);

  useEffect(() => {
    if (isAuthenticated && !authLoading && !code && !tenantId) {
      navigate('/');
    }
  }, [isAuthenticated, authLoading, navigate, code, tenantId]);

  useEffect(() => {
    if (tenantId) {
      sessionStorage.removeItem(SKIP_SSO_AUTO_KEY);
      setSkipAutoSso(false);
    }
  }, [tenantId]);

  useEffect(() => {
    if (code || oauthError || startedSso.current) return;
    if (!sso || !effectiveConfig.identity_url) return;
    if (skipAutoSso && !tenantId) return;
    startedSso.current = true;
    startIdentityLogin(effectiveConfig).catch((err) => {
      setError('שגיאה בהתחברות. נא לנסות שוב.');
      showError('שגיאה בהתחברות', {
        action: 'Start SSO login',
        error: err,
      });
      startedSso.current = false;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sso, effectiveConfig.identity_url, code, oauthError, skipAutoSso, tenantId]);

  const handleOAuthCallback = async (authCode: string, state: string | null) => {
    setIsLoading(true);
    setError('');

    try {
      const packed = decodeOauthState(state) || readOauthCookie();
      const verifier =
        packed?.v ||
        sessionStorage.getItem(OAUTH_VERIFIER_KEY) ||
        localStorage.getItem(OAUTH_VERIFIER_KEY);
      const expectedNonce =
        sessionStorage.getItem(OAUTH_STATE_KEY) || localStorage.getItem(OAUTH_STATE_KEY);
      clearOauth();

      const useSso = sso || Boolean(verifier);
      if (useSso) {
        if (!verifier) {
          throw new Error('Missing PKCE verifier');
        }
        if (expectedNonce && packed?.n && expectedNonce !== packed.n) {
          throw new Error('Invalid OAuth state');
        }
        await login(authCode, {
          codeVerifier: verifier,
          redirectUri: callbackRedirectUri(),
        });
      } else {
        await login(authCode);
      }
      navigate('/');
    } catch (err) {
      const message = err instanceof Error ? err.message : '';
      setError(message && message !== 'SSO login requires PKCE'
        ? message
        : 'שגיאה בהתחברות. נא לנסות שוב.');
      showError('שגיאה בהתחברות', {
        action: 'OAuth callback',
        error: err,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const startIdentityLogin = async (cfg: AuthModeConfig) => {
    const { verifier, challenge } = await pkcePair();
    const nonce = randomUrlSafe(24);
    const packed: OauthState = { n: nonce, v: verifier };
    persistOauth(packed);
    const params = new URLSearchParams({
      client_id: cfg.client_id || 'expenses',
      redirect_uri: callbackRedirectUri(),
      response_type: 'code',
      state: encodeOauthState(packed),
      code_challenge: challenge,
      code_challenge_method: 'S256',
    });
    if (tenantId) params.set('tenant_id', tenantId);
    window.location.href = `${(cfg.identity_url || '').replace(/\/$/, '')}/oauth/authorize?${params.toString()}`;
  };

  const handleGoogleLogin = () => {
    const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
      `client_id=${config.oauth.google.clientId}&` +
      `redirect_uri=${encodeURIComponent(config.oauth.google.redirectUri)}&` +
      `response_type=code&` +
      `scope=openid email profile&` +
      `access_type=offline&` +
      `prompt=consent`;
    
    window.location.href = googleAuthUrl;
  };

  const beginAccountsLogin = () => {
    sessionStorage.removeItem(SKIP_SSO_AUTO_KEY);
    setSkipAutoSso(false);
    startIdentityLogin(effectiveConfig).catch((err) => {
      setError('שגיאה בהתחברות. נא לנסות שוב.');
      showError('שגיאה בהתחברות', {
        action: 'Start Accounts login',
        error: err,
      });
    });
  };

  if (sso && skipAutoSso && !code && !tenantId) {
    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.logoSection}>
            <div className={styles.logo}>
              <span className={styles.logoText}>Metrica</span>
            </div>
            <div className={styles.logoSubtitle}>ניהול הוצאות</div>
          </div>
          <div className={styles.formSection}>
            <h1 className={styles.title}>התנתקתם</h1>
            <p className={styles.subtitle}>התחברו שוב דרך Accounts כדי להמשיך</p>
            {error && (
              <div className={styles.errorMessage}>
                {error}
              </div>
            )}
            <button
              type="button"
              onClick={beginAccountsLogin}
              disabled={isLoading || !effectiveConfig.identity_url}
              className={styles.googleButton}
            >
              {isLoading ? 'מתחבר...' : 'התחבר'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (sso || code) {
    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.logoSection}>
            <div className={styles.logo}>
              <span className={styles.logoText}>Metrica</span>
            </div>
            <div className={styles.logoSubtitle}>ניהול הוצאות</div>
          </div>
          <div className={styles.formSection}>
            <h1 className={styles.title}>מתחבר...</h1>
            {error && (
              <div className={styles.errorMessage}>
                {error}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.logoSection}>
          <div className={styles.logo}>
            <span className={styles.logoText}>Metrica</span>
          </div>
          <div className={styles.logoSubtitle}>ניהול הוצאות</div>
        </div>

        <div className={styles.formSection}>
          <h1 className={styles.title}>ברוכים הבאים</h1>
          <p className={styles.subtitle}>התחברו כדי לנהל הוצאות, חשבוניות וקטגוריות במקום אחד</p>

          <div className={styles.form}>
            {error && (
              <div className={styles.errorMessage}>
                {error}
              </div>
            )}

            <button
              type="button"
              onClick={handleGoogleLogin}
              disabled={isLoading}
              className={styles.googleButton}
            >
              <svg className={styles.googleIcon} viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {isLoading ? 'מתחבר...' : 'התחבר עם Google'}
            </button>
          </div>

          <div className={styles.footer}>
            <p className={styles.footerText}>
              אין לך חשבון? <span className={styles.link}>צור קשר עם המנהל</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignInPage;
